/**
 * Database RPC Middleware Tag
 *
 * RpcMiddleware tag that provides the Drizzle `Database` to RPC handlers.
 * The implementation is provided by the app using ServiceMap.Reference.
 *
 * Database is imported as type-only so client bundlers (Vite) don't
 * try to resolve @repo/db at runtime. The error type is defined inline
 * to avoid pulling in @repo/domain.
 *
 * @module
 */
import { Schema as S } from "effect"
import { RpcMiddleware } from "effect/unstable/rpc"
import type { Database } from "@repo/db/database"

export class DatabaseConnectionError extends S.TaggedErrorClass<DatabaseConnectionError>()(
  "DatabaseConnectionError",
  { message: S.String },
) {}

/**
 * Middleware that provides the Drizzle `Database` to RPC handlers. Transactions
 * run via `db.transaction(...)`, so no separate SqlClient is exposed.
 *
 * Apply to RPC procedures that need database access:
 *
 * ```typescript
 * const myRpc = Rpc.make("myRpc", { ... })
 *   .middleware(RpcDatabaseMiddleware)
 * ```
 */
export class RpcDatabaseMiddleware extends RpcMiddleware.Service<
  RpcDatabaseMiddleware,
  { provides: Database }
>()("@repo/rpc/RpcDatabaseMiddleware", {
  error: DatabaseConnectionError,
  requiredForClient: false
}) {}
